import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useConfirm } from "../../lib/confirm";
import { apiErrorMessage } from "../../lib/errors";
import { EmptyState } from "../../components/empty-state";

interface ScheduledPost {
  id: string;
  social_account_id: string;
  variant_id: string | null;
  project_id: string | null;
  platform: string;
  content: string;
  scheduled_for: number;
  status: "pending" | "publishing" | "published" | "failed";
  external_post_url: string | null;
  error_message: string | null;
  attempts: number;
  published_at: number | null;
  created_at: number;
}

const STATUS_LABEL: Record<ScheduledPost["status"], string> = {
  pending: "Pending",
  publishing: "Publishing…",
  published: "Published",
  failed: "Failed",
};

const STATUS_COLOR: Record<ScheduledPost["status"], string> = {
  pending: "bg-blue-100 text-blue-800",
  publishing: "bg-amber-100 text-amber-800",
  published: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
};

const PLATFORM_LABEL: Record<string, string> = {
  twitter: "X / Twitter",
  bluesky: "Bluesky",
  linkedin: "LinkedIn",
  newsletter: "Newsletter",
  short_video: "Short video",
};

const PLATFORM_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "twitter", label: "X" },
  { value: "bluesky", label: "Bluesky" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "newsletter", label: "Newsletter" },
  { value: "short_video", label: "Short video" },
];

export default function SchedulePage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] =
    useState<"all" | "pending" | "published" | "failed">("all");
  const [platformFilter, setPlatformFilter] = useState<string>("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (platformFilter) params.set("platform", platformFilter);
      params.set("limit", "500");
      const data = await api.get<{ scheduled_posts: ScheduledPost[] }>(
        `/api/scheduled-posts?${params.toString()}`
      );
      setPosts(data.scheduled_posts ?? []);
      setSelected((prev) => {
        const next = new Set<string>();
        const ids = new Set((data.scheduled_posts ?? []).map((p) => p.id));
        prev.forEach((id) => {
          if (ids.has(id)) next.add(id);
        });
        return next;
      });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, platformFilter]);

  const deletableIds = useMemo(
    () =>
      posts
        .filter((p) => p.status === "pending" || p.status === "failed")
        .map((p) => p.id),
    [posts]
  );

  const selectedDeletable = useMemo(
    () => Array.from(selected).filter((id) => deletableIds.includes(id)),
    [selected, deletableIds]
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllDeletable() {
    setSelected(new Set(deletableIds));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function cancel(id: string) {
    const ok = await confirm({
      title: "Cancel scheduled post",
      message: "This post will be removed from the queue and won't be published.",
      confirmLabel: "Cancel post",
      cancelLabel: "Keep",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/scheduled-posts/${id}`);
      toast.success("Cancelled.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function bulkDelete() {
    if (selectedDeletable.length === 0) return;
    const n = selectedDeletable.length;
    const ok = await confirm({
      title: `Delete ${n} scheduled post${n === 1 ? "" : "s"}`,
      message: "Already-published posts will be skipped automatically.",
      confirmLabel: `Delete ${n}`,
      destructive: true,
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await api.post<{ deleted: number }>(
        "/api/scheduled-posts/bulk-delete",
        { ids: selectedDeletable }
      );
      toast.success(
        `Deleted ${res.deleted} post${res.deleted === 1 ? "" : "s"}.`
      );
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBulkBusy(false);
    }
  }

  const allDeletableSelected =
    deletableIds.length > 0 &&
    deletableIds.every((id) => selected.has(id));

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="page-title">Schedule</h1>
          <p className="page-subtitle">
            Posts you've queued for auto-publishing across connected social
            accounts.
          </p>
        </div>
        <div className="flex gap-1 rounded-full bg-ink-100 p-1 text-xs">
          {(["all", "pending", "published", "failed"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`rounded-full px-3 py-1.5 transition-colors ${
                statusFilter === f
                  ? "bg-white text-ink-900 shadow-sm"
                  : "text-ink-500 hover:text-ink-900"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-500">Platform:</span>
        {PLATFORM_FILTERS.map((p) => (
          <button
            key={p.value || "all"}
            onClick={() => setPlatformFilter(p.value)}
            className={`rounded-full px-3 py-1 text-xs transition-colors ${
              platformFilter === p.value
                ? "bg-ink-900 text-white"
                : "bg-ink-100 text-ink-600 hover:bg-ink-200"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {!loading && deletableIds.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-ink-200/70 bg-white px-3.5 py-2.5 text-sm shadow-xs">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 cursor-pointer rounded border-ink-300 text-brand-600 focus:ring-brand-400"
              checked={allDeletableSelected}
              onChange={(e) => {
                if (e.target.checked) selectAllDeletable();
                else clearSelection();
              }}
            />
            <span className="text-ink-700">
              {selected.size > 0
                ? `${selected.size} selected`
                : `Select all (${deletableIds.length})`}
            </span>
          </label>
          {selectedDeletable.length > 0 ? (
            <>
              <button
                onClick={clearSelection}
                className="text-xs text-ink-500 hover:text-ink-900"
              >
                Clear
              </button>
              <span className="ml-auto" />
              <button
                onClick={bulkDelete}
                disabled={bulkBusy}
                className="btn-danger btn-sm"
              >
                {bulkBusy
                  ? "Deleting…"
                  : `Delete ${selectedDeletable.length} selected`}
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-ink-500">Loading…</p>
      ) : posts.length === 0 ? (
        <EmptyState
          icon={<CalendarClock className="h-6 w-6" />}
          title="Nothing scheduled (yet)"
          description="When you Schedule a variant from a project, or import a CSV into the queue, it shows up here in chronological order."
          action={
            <Link to="/app" className="btn-brand no-underline">
              Browse projects
            </Link>
          }
        />
      ) : (
        <ul className="space-y-3">
          {posts.map((p) => {
            const canDelete = p.status === "pending" || p.status === "failed";
            return (
              <li
                key={p.id}
                className="rounded-xl border border-ink-200/70 bg-white p-4 shadow-xs transition-all hover:border-ink-200 hover:shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    {canDelete ? (
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 cursor-pointer rounded border-ink-300 text-brand-600 focus:ring-brand-400"
                        checked={selected.has(p.id)}
                        onChange={() => toggle(p.id)}
                      />
                    ) : (
                      <span className="mt-1 inline-block h-4 w-4" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span
                          className={`rounded-full px-2 py-0.5 font-medium ${STATUS_COLOR[p.status]}`}
                        >
                          {STATUS_LABEL[p.status]}
                        </span>
                        <span className="text-ink-500">
                          {PLATFORM_LABEL[p.platform] ?? p.platform}
                        </span>
                        <span className="text-ink-500">·</span>
                        <span className="text-ink-700">
                          {p.status === "published" && p.published_at
                            ? `Published ${new Date(p.published_at).toLocaleString()}`
                            : `Scheduled ${new Date(p.scheduled_for).toLocaleString()}`}
                        </span>
                        {p.attempts > 0 && p.status !== "published" ? (
                          <span className="text-ink-500">
                            · {p.attempts} attempt{p.attempts === 1 ? "" : "s"}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-ink-900">
                        {p.content}
                      </p>
                      {p.error_message ? (
                        <p className="mt-2 text-xs text-red-600">
                          Error: {p.error_message}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {p.external_post_url ? (
                      <a
                        href={p.external_post_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-ink-500 hover:text-ink-900"
                      >
                        View post →
                      </a>
                    ) : null}
                    {p.project_id ? (
                      <Link
                        to={`/app/projects/${p.project_id}`}
                        className="text-xs text-ink-500 hover:text-ink-900"
                      >
                        Open project →
                      </Link>
                    ) : null}
                    {canDelete ? (
                      <button
                        onClick={() => cancel(p.id)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Cancel
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
